import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, Pipe, PipeTransform, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ComponentBaseComponent } from '../component-base/component-base.component';
import { TranslateService } from '@app/core/services/translate.service';
import { DataTaskService } from '@app/core/services/data-task.service';
import { CdkDrag, CdkDragDrop, CdkDragMove, CdkDragRelease, CdkDragStart, CdkDropList, copyArrayItem, transferArrayItem } from '@angular/cdk/drag-drop';
import { PlannerEventComponent } from './planner-event/planner-event.component';

@Component({
    selector: 'tt-planner',
    templateUrl: './planner.component.html',
    styleUrls: ['./planner.component.css'],
})
export class PlannerComponent<TType extends { [key: string]: string }, AType extends { [key: string]: string }> extends ComponentBaseComponent implements OnInit, OnChanges, AfterViewInit, AfterContentInit {
    @ViewChild('table')
    private table?: ElementRef<HTMLTableElement>;

    @ViewChild('viewport')
    private viewport?: ElementRef<HTMLDivElement>;

    @ViewChildren(CdkDrag)
    private dragitems?: QueryList<CdkDrag>;

    @ContentChildren(CdkDrag, { descendants: true })
    private resourceItems?: QueryList<CdkDrag>;

    @ContentChildren(CdkDropList, { descendants: true })
    private resourceDroplists?: QueryList<CdkDropList>;

    @ViewChildren(CdkDropList)
    private droplists?: QueryList<CdkDropList>;

    @ViewChildren(PlannerEventComponent)
    private events?: QueryList<PlannerEventComponent>;

    // #region INPUTS

    @Input()
    public ttResourceList: string[] = [];

    /**
     * List of items that are scheduled and should contain new schedules.
     */
    @Input()
    public ttData: TType[] = [];

    /**
     * Event emitted when a change to the schedule has occurred.
     */
    @Output()
    public ttDataChange = new EventEmitter<TType[]>();

    /**
     * The activities the items should be scheduled on.
     */
    @Input()
    public ttActivities: AType[] = [];

    /**
     * The default start time for newly added tasks.
     */
    @Input()
    public ttStartTime: string = '07:00';

    /**
     * The default end time for newly added tasks.
     */
    @Input()
    public ttEndTime: string = '15:00';

    /**
     * The name of the property holding the id of the event.
     *
     * @default 'item_id'
     */
    @Input()
    public ttDataId: keyof TType = 'item_id';

    /**
     * The name of the property holding the start datetime of the event.
     *
     * @default 'jobtime_start_dt'
     */
    @Input()
    public ttDataStartDatetime: keyof TType = 'jobtime_start_dt' as keyof TType;

    /**
     * The name of the property holding the end datetime of the event.
     *
     * @default 'jobtime_end_dt'
     */
    @Input()
    public ttDataEndDatetime: keyof TType = 'jobtime_end_dt' as keyof TType;

    /**
     * The name of the property holding the date of the event.
     *
     * @default 'jobtime_scalevalue'
     */
    @Input()
    public ttDataDate: keyof TType = 'jobtime_scalevalue' as keyof TType;

    // #endregion INPUTS

    private droplistIds: string[] = [];

    public connectedListIds: string[] = [];

    public deletableListIds: string[] = [];

    selectedTimescale = '7';

    loading = false;

    translations: { [key: string]: string } = {
        earlier: '',
        later: '',
    };

    equipments = EQUIPMENTS;

    dropzones: { [key: string]: DropZone<TType>[] } = {};

    ttFilterValue: string = '';

    constructor(private datatask: DataTaskService, private translateService: TranslateService, private cdr: ChangeDetectorRef) {
        super();
        // @ts-ignore
        translateService.translateBatch(this.translations).then((data) => (this.translations = data));
    }

    // #region RESIZE

    /**
     * The width of the activities column.
     */
    public taskcolwidth = '120px';

    /**
     * The distance between the pointer x and the leftside of the element.
     */
    private offset = 0;

    /**
     * Whether the column is currently being resized.
     */
    public resizing = false;

    @HostListener('document:pointermove', ['$event'])
    public onDocumentPointermove(event: PointerEvent) {
        if (this.resizing) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();

            this.taskcolwidth = `${+event.clientX - (this.table?.nativeElement.getBoundingClientRect().left ?? 0) - this.offset - (this.viewport?.nativeElement.scrollLeft ?? 0)}px`;
        }
    }

    @HostListener('document:pointerup', ['$event'])
    public onDocumentPointerup(_: PointerEvent) {
        this.resizing = false;
        document.body.style.cursor = 'initial';
    }

    public startResize(event: PointerEvent) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();

        this.resizing = true;

        document.body.style.cursor = 'col-resize';

        if (event.target) this.offset = event.clientX - (event.target as HTMLTableCellElement).getBoundingClientRect().left;
    }

    // #endregion RESIZE

    // #region NESTED DROPLIST

    /**
     * The id of the currently hovered list.
     */
    private currentHoveredDropListId: string | null = null;

    /**
     * Whether a recheck of the enter predicate has already occurred for the ongoing drag event for the currently hovered drag item.
     */
    public rechecked = false;

    /**
     * The enter predicate for the top level droplists.
     */
    public isDropAllowed = (_: CdkDrag, drop: CdkDropList) => {
        if (this.currentHoveredDropListId == null) {
            return true;
        }

        return drop.id === this.currentHoveredDropListId;
    };

    isDragging = false;

    /**
     * Finds the currently hovered list.
     *
     * @param event the move event.
     */
    public dragMoved(event: CdkDragMove) {
        let elementsFromPoint = document.elementsFromPoint(event.pointerPosition.x, event.pointerPosition.y);
        let elementFromPoint = elementsFromPoint.find((element) => element.classList.contains('innerlist')) ?? elementsFromPoint[0];

        if (!elementFromPoint) {
            this.currentHoveredDropListId = null;
            return;
        }

        const dropList = elementFromPoint.classList.contains('cdk-drop-list') ? elementFromPoint : elementFromPoint.closest('.cdk-drop-list');

        if (!dropList) {
            this.currentHoveredDropListId = null;
            return;
        }

        this.currentHoveredDropListId = dropList.id;
    }

    /**
     * Resets the currently hovered droplist id.
     */
    public dragReleased(_: CdkDragRelease) {
        this.currentHoveredDropListId = null;
        this.isDragging = false;
    }

    /**
     * Forces the enter predicate of the currently hovered top-level droplist to recheck, as nested list are not supported by the cdk.
     *
     * @param event the pointerevent when an item in a top level dropzone as been hovered over while pressing pointer down.
     */
    public forceEnterPredicateCheck(event: MouseEvent) {
        if (!this.rechecked) {
            // @ts-ignore
            const drag = this.dragitems?.find((d) => d._dragRef._hasStartedDragging);
            // @ts-ignore
            const resourcedrag = this.resourceItems?.find((d) => d._dragRef._hasStartedDragging);
            const draggedItem = resourcedrag ?? drag;

            if (!!draggedItem) {
                this.rechecked = true;
                // @ts-ignore
                draggedItem._dragRef._endDragSequence(event);
            }
        }
    }

    // #endregion NESTED DROPLIST

    dragStart(event: CdkDragStart) {
        this.isDragging = true;
        const elementRect = event.source.dropContainer.element.nativeElement.getBoundingClientRect();
        let clientX: number | null = null;

        if ('clientX' in event.event) {
            clientX = event.event.clientX;
        } else if ('touches' in event.event && event.event.touches.length > 0) {
            clientX = event.event.touches[0].clientX;
        }

        if (clientX !== null && clientX > elementRect.left && clientX < elementRect.left + elementRect.width / 2) {
            const index = event.source.dropContainer.data.findIndex((item: TType) => item[this.ttDataId] === event.source.data[this.ttDataId]);
            copyArrayItem(event.source.dropContainer.data, event.source.dropContainer.data, index, index);
        }
    }

    dropDropZone(event: CdkDragDrop<TType[], TType[], TType>, dropzone: DropZone<TType>) {
        console.log(event);
        if (event.previousContainer.id === 'resources') {
            event.item.data[this.ttDataDate] = dropzone.date as TType[keyof TType];
            event.item.data[this.ttDataStartDatetime] = (dropzone.date + ' ' + this.ttStartTime) as TType[keyof TType];
            event.item.data[this.ttDataEndDatetime] = (dropzone.date + ' ' + this.ttEndTime) as TType[keyof TType];

            copyArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
        } else {
            event.item.data;
            event.item.data[this.ttDataDate] = dropzone.date as TType[keyof TType];
            event.item.data[this.ttDataStartDatetime] = (dropzone.date + ' ' + event.item.data[this.ttDataStartDatetime].substring(11)) as TType[keyof TType];
            event.item.data[this.ttDataEndDatetime] = (dropzone.date + ' ' + event.item.data[this.ttDataEndDatetime].substring(11)) as TType[keyof TType];

            transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
        }

        this.droplistIds = this.droplistIds.filter((list) => list !== event.previousContainer.id + event.item.data[this.ttDataId] + event.previousIndex);
        this.droplistIds.push(event.container.id + event.item.data[this.ttDataId] + event.currentIndex);

        this.refreshConnectListIds();
        this.mapResourcesToNewList();
    }

    private refreshConnectListIds() {
        this.connectedListIds = [...(this.resourceDroplists?.map((item) => item.id) ?? []), ...(this.droplists?.map((item) => item.id) ?? []), ...(this.events?.map((item) => item.ttId) ?? [])];
        this.deletableListIds = [...(this.droplists?.map((item) => item.id) ?? []), ...(this.events?.map((item) => item.ttId) ?? [])];
        console.log('this.connectedListIds :>> ', this.connectedListIds);
        // this.connectedListIds = Array.from(new Set([...this.ttResourceList, ...this.droplistIds]));
    }

    public deleteItem(event: CdkDragDrop<TType[], TType[], TType>) {
        // event.previousContainer.data = event.previousContainer.data.filter((item) => item[this.ttDataId] === event.item.data[this.ttDataId]);
        transferArrayItem(event.previousContainer.data, event.container.data ?? [], event.previousIndex, event.currentIndex);
    }

    log(event: any) {
        console.log(event);
    }

    private mapResourcesToNewList() {
        const items = Object.values(this.dropzones)
            .map((dropzones) => dropzones.map((dropzone) => dropzone.resources))
            .flat(3) as TType[];
        console.log('items :>> ', items);
        // this.ttItemsChange.emit(this.ttItems);
    }

    // async onTimescaleChanged() {
    //     this.loading = true;
    //     await this.loadTimescale();
    //     this.loading = false;
    // }

    trackByTimescale(_: number, timescale: any) {
        return timescale.timescale_value;
    }

    trackByEquipment(_: number, equipment: any) {
        return equipment.item_id;
    }

    trackByDropzone(_: number, dropzone: DropZone<TType>) {
        return dropzone.date + dropzone.resourceId;
    }

    async loadTimescale(params?: Partial<{ nbr_of_columns: string; fom_date: string; direction: 'UP' | 'DOWN' }>) {
        this.timescales = await this.datatask.Post(303, {
            nbr_of_columns: this.selectedTimescale,
            fom_date: 'today',
            direction: '',
            ...params,
        });

        this.createDropzones();
    }

    private createDropzones() {
        for (let equipment of this.equipments) {
            for (let timescale of this.timescales) {
                if (!this.dropzones[equipment.item_id]) {
                    this.dropzones[equipment.item_id] = [];
                }

                if (!this.dropzones[equipment.item_id].some((dropzone) => dropzone.id === equipment.item_id + timescale.timescale_value)) {
                    this.droplistIds.push(equipment.item_id + timescale.timescale_value);
                    this.dropzones[equipment.item_id].push({ id: equipment.item_id + timescale.timescale_value, date: timescale.timescale_value, style: {}, resourceId: equipment.item_id, isWeekend: timescale.timescale_heading_color === 'red' || timescale.timescale_heading_color === 'blue', resources: [], resources2: [] });
                }
            }

            this.dropzones[equipment.item_id].forEach((dropzone) => (dropzone.hidden = new Date(dropzone.date) < new Date(this.timescales[0].timescale_value) || new Date(dropzone.date) > new Date(this.timescales[this.timescales.length - 1].timescale_value)));
        }

        this.refreshConnectListIds();
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;

        await this.loadTimescale();

        this.loading = false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttResourceList']) {
            this.refreshConnectListIds();
        }
    }

    ngAfterViewInit(): void {
        this.droplists?.changes.subscribe({ next: () => this.refreshConnectListIds() });
        this.events?.changes.subscribe({ next: () => this.refreshConnectListIds() });
        this.refreshConnectListIds();
    }

    ngAfterContentInit(): void {
        this.resourceItems?.forEach((item) => {
            item.moved.subscribe({ next: (event) => this.dragMoved(event) });
            item.released.subscribe({ next: (event: CdkDragRelease<any>) => this.dragReleased(event) });
        });

        this.resourceItems?.changes.subscribe({ next: () => this.refreshConnectListIds() });
        this.refreshConnectListIds();
    }

    timescales = TIMESCALES;
}

interface DropZone<TType extends { [key: string]: string }> {
    id: string;
    style: Partial<CSSStyleDeclaration>;
    date: string;
    resourceId: string;
    resources: TType[];
    resources2: TType[];
    isWeekend: boolean;
    disable?: boolean;
    hidden?: boolean;
}

@Pipe({
    name: 'plannerDropzoneFilter',
})
export class PlannerResourceFilter implements PipeTransform {
    transform(items: any[], filter: string): any {
        if (!items || !filter) {
            return items;
        }
        console.dir(items[0]);
        // filter items array, items which match and return true will be
        // kept, false will be filtered out
        return items.filter((item) => Object.values(item).toString().toLowerCase().includes(filter.toLowerCase()));
    }
}

const TIMESCALES = [
    {
        timescale_value: '2025-04-08',
        timescale_label: 'Mandag 8.4.',
        timescale_heading_color: 'Black',
        timescale_heading_backcolor: '',
        background: '',
    },
    {
        timescale_value: '2025-04-09',
        timescale_label: 'Tirsdag 9.4.',
        timescale_heading_color: 'Black',
        timescale_heading_backcolor: '',
    },
    {
        timescale_value: '2025-04-10',
        timescale_label: 'Onsdag 10.4.',
        timescale_heading_color: 'Black',
        timescale_heading_backcolor: '',
    },
    {
        timescale_value: '2025-04-11',
        timescale_label: 'Torsdag 11.4.',
        timescale_heading_color: 'Black',
        timescale_heading_backcolor: '',
    },
    {
        timescale_value: '2025-04-12',
        timescale_label: 'Fredag 12.4.',
        timescale_heading_color: 'Black',
        timescale_heading_backcolor: '',
    },
    {
        timescale_value: '2025-04-13',
        timescale_label: 'Lørdag 13.4.',
        timescale_heading_color: 'blue',
        timescale_heading_backcolor: '',
    },
    {
        timescale_value: '2025-04-14',
        timescale_label: 'Søndag 14.4.',
        timescale_heading_color: 'red',
        timescale_heading_backcolor: '',
    },
];

const EQUIPMENTS = [
    {
        item_id: 'enm',
        item_name: 'Ensy maskin ',
        orderby: '17',
    },
    {
        item_id: '0021',
        item_name: 'Forsikring 123 ',
        orderby: '18',
    },
    {
        item_id: '030',
        item_name: 'Forskuddsbetalt husleie 12md ',
        orderby: '19',
    },
    {
        item_id: 'rif',
        item_name: 'Fres 1 ',
        orderby: '20',
    },
    {
        item_id: 'GAL',
        item_name: 'Galvanisering ',
        orderby: '21',
    },
    {
        item_id: 'G001',
        item_name: 'Gravemaskin ',
        orderby: '22',
    },
    {
        item_id: 'uf24284',
        item_name: 'Hond crv ',
        orderby: '23',
    },
    {
        item_id: 'kan',
        item_name: 'Kan ny endret ',
        orderby: '24',
    },
    {
        item_id: 'KP001',
        item_name: 'KANTPERFORERING Poul Möller  3 ',
        orderby: '25',
    },
    {
        item_id: 'KA001',
        item_name: 'KANTPRESSE BEYELER NC-STYRT 2 ',
        orderby: '26',
    },
    {
        item_id: 'KA002',
        item_name: 'KANTPRESSE LVD  2m 60t ',
        orderby: '27',
    },
    {
        item_id: '777',
        item_name: 'kjv ',
        orderby: '28',
    },
    {
        item_id: 'KL001',
        item_name: 'KLIPPEMASKIN NORCO FOR TRÅDHYL ',
        orderby: '29',
    },
    {
        item_id: '504',
        item_name: 'Leadwell LTC-50CXL dreiebenk ',
        orderby: '30',
    },
    {
        item_id: '503',
        item_name: 'Mas Stu 40CNC dreiebenk ',
        orderby: '31',
    },
    {
        item_id: 'MA1',
        item_name: 'Maskinering 1 ',
        orderby: '32',
    },
    {
        item_id: 'MA2',
        item_name: 'Maskinering 2 ',
        orderby: '33',
    },
    {
        item_id: 'MOVI',
        item_name: 'Montere vindu ',
        orderby: '34',
    },
    {
        item_id: 'mv',
        item_name: 'Montering stasjon 1 ',
        orderby: '35',
    },
    {
        item_id: 'MS2',
        item_name: 'Montering stasjon 2 ',
        orderby: '36',
    },
    {
        item_id: 'MOBE',
        item_name: 'Monteringsbenk ',
        orderby: '37',
    },
    {
        item_id: 'mg',
        item_name: 'Monteringsjigg Gets ',
        orderby: '38',
    },
    {
        item_id: 'AMT1',
        item_name: 'Multiaxial 001 ',
        orderby: '39',
    },
    {
        item_id: 'AMT2',
        item_name: 'Multiaxial 002 ',
        orderby: '40',
    },
    {
        item_id: 'AMT3',
        item_name: 'Multiaxial 003 ',
        orderby: '41',
    },
    {
        item_id: 'AMT4',
        item_name: 'Multiaxial 004 ',
        orderby: '42',
    },
    {
        item_id: 'OWA',
        item_name: 'OWA ',
        orderby: '43',
    },
    {
        item_id: 'OWB',
        item_name: 'OWB ',
        orderby: '44',
    },
    {
        item_id: 'OWC',
        item_name: 'OWC ',
        orderby: '45',
    },
    {
        item_id: '1981',
        item_name: 'OYSTEST Copra test',
        orderby: '46',
    },
    {
        item_id: 'pabo',
        item_name: 'Pakkebord ',
        orderby: '47',
    },
    {
        item_id: 'pg',
        item_name: 'Pakkelinje Gets ',
        orderby: '48',
    },
    {
        item_id: 'PU002',
        item_name: 'PUNKTSVEISE DALEX PL80 80kwA ',
        orderby: '49',
    },
    {
        item_id: 'PU001',
        item_name: 'PUNKTSVEISE UNITOR 1B12 ',
        orderby: '50',
    },
    {
        item_id: '003',
        item_name: 'SAG Eisele Halvautomat ',
        orderby: '51',
    },
    {
        item_id: 'SG003',
        item_name: 'SAG Eisele Halvautomat ',
        orderby: '52',
    },
    {
        item_id: '002',
        item_name: 'SAG Manuell sag med 10sk ',
        orderby: '53',
    },
    {
        item_id: 'SG002',
        item_name: 'SAG Manuell sag uten produkt ',
        orderby: '54',
    },
    {
        item_id: 'SG001',
        item_name: 'SAG Simec Automatsag MEC 120 B ',
        orderby: '55',
    },
    {
        item_id: 'SA002',
        item_name: 'SAKS DMF  1m t=4mm ',
        orderby: '56',
    },
    {
        item_id: 'SP001',
        item_name: 'SIDEPERFORERING Poul Möller  2 ',
        orderby: '57',
    },
    {
        item_id: 'SL002',
        item_name: 'SLIPEMASKIN Bandsliper ',
        orderby: '58',
    },
    {
        item_id: 'SL001',
        item_name: 'SLIPEMASKIN NORCO ',
        orderby: '59',
    },
    {
        item_id: '004',
        item_name: 'SLIPEMASKIN Smergelskive ',
        orderby: '60',
    },
    {
        item_id: 'SL004',
        item_name: 'SLIPEMASKIN Smergelskive ',
        orderby: '61',
    },
    {
        item_id: 'SL003',
        item_name: 'SLIPEMASKIN Vinkelsliper ',
        orderby: '62',
    },
    {
        item_id: 'ss1',
        item_name: 'sstest ',
        orderby: '63',
    },
    {
        item_id: 'ST003',
        item_name: 'STANSE DPF  40t ',
        orderby: '64',
    },
    {
        item_id: 'ST010',
        item_name: 'STANSE Poul Möller  15t ',
        orderby: '65',
    },
    {
        item_id: '010',
        item_name: 'STANSE Poul Möller  15t btomet ',
        orderby: '66',
    },
    {
        item_id: 'ST001',
        item_name: 'STANSE Poul Müller  100t ',
        orderby: '67',
    },
    {
        item_id: '5',
        item_name: 'STANSE Poul Müller  15t ',
        orderby: '68',
    },
    {
        item_id: 'ST007',
        item_name: 'STANSE Poul Müller  15t ',
        orderby: '69',
    },
    {
        item_id: 'ST008',
        item_name: 'STANSE Poul Müller  15t ',
        orderby: '70',
    },
    {
        item_id: 'ST009',
        item_name: 'STANSE Poul Müller  15t ',
        orderby: '71',
    },
    {
        item_id: 'ST006',
        item_name: 'STANSE Poul Müller  22t ',
        orderby: '72',
    },
    {
        item_id: 'ST004',
        item_name: 'STANSE Poul Müller  45t ',
        orderby: '73',
    },
    {
        item_id: 'ST002',
        item_name: 'STANSE Poul Müller  60t ',
        orderby: '74',
    },
    {
        item_id: 'ST005',
        item_name: 'STANSE SMERAL  160t ',
        orderby: '75',
    },
    {
        item_id: 'kap-a',
        item_name: 'Storsag ',
        orderby: '76',
    },
    {
        item_id: '40',
        item_name: 'Sveis 1 ',
        orderby: '77',
    },
    {
        item_id: 'SV002',
        item_name: 'SVEISEMASKIN   Vallius ',
        orderby: '78',
    },
    {
        item_id: 'SV003',
        item_name: 'SVEISEMASKIN ESAB LDA200 ',
        orderby: '79',
    },
    {
        item_id: 'SV005',
        item_name: 'SVEISEMASKIN Kempi Kempomat 25 ',
        orderby: '80',
    },
    {
        item_id: 'SV006',
        item_name: 'SVEISEMASKIN Kempi Kempomat 25 ',
        orderby: '81',
    },
    {
        item_id: 'SV007',
        item_name: 'SVEISEMASKIN Kempi Kempomat 25 ',
        orderby: '82',
    },
    {
        item_id: 'SV008',
        item_name: 'SVEISEMASKIN Kempi Kempomat 25 ',
        orderby: '83',
    },
    {
        item_id: '005',
        item_name: 'SVEISEMASKIN Kempi Kempomat 250 005 ',
        orderby: '84',
    },
    {
        item_id: '006',
        item_name: 'SVEISEMASKIN Kempi Kempomat 250 006 ',
        orderby: '85',
    },
    {
        item_id: '007',
        item_name: 'SVEISEMASKIN Kempi Kempomat 250 007 ',
        orderby: '86',
    },
    {
        item_id: '008',
        item_name: 'SVEISEMASKIN Kempi Kempomat 250 008 ',
        orderby: '87',
    },
    {
        item_id: 'SV001',
        item_name: 'SVEISEMASKIN NORCO STUKSVEIS ',
        orderby: '88',
    },
    {
        item_id: 'SR001',
        item_name: 'Sveiserobot 1 ',
        orderby: '89',
    },
    {
        item_id: 'SR002',
        item_name: 'Sveiserobot 2 ',
        orderby: '90',
    },
];
